// Client-side parsing of the streamed door-build output. The raw model text is
// an optional INVENTED/NO FIT lead line followed by the full HTML document —
// the contract described in lib/doors/buildPrompt.ts. Ported from the Kula
// Sandbox (kula-sandbox/src/lib/parseBuildOutput.ts + extractHtml.ts).

export type ParsedDoorBuild =
  | { kind: 'built'; htmlContent: string; inventedNote: string | null }
  | { kind: 'no-fit'; verdict: string };

/** Defensive extraction — the prompt says "ONLY the HTML file, no fences",
 *  but strip a fence or preamble just in case. */
export function extractHtml(raw: string): string {
  let text = raw.trim();

  const fenceMatch = text.match(/```(?:html)?\s*([\s\S]*?)\s*```/i);
  if (fenceMatch) {
    text = fenceMatch[1].trim();
  }

  const docStart = text.search(/<!doctype html|<html\b/i);
  if (docStart > 0) {
    text = text.slice(docStart).trim();
  }

  return text;
}

export function parseDoorBuildOutput(raw: string): ParsedDoorBuild {
  const trimmed = raw.trim();
  if (!hasDoctype(raw) && /^NO FIT:/i.test(trimmed)) {
    return { kind: 'no-fit', verdict: trimmed.replace(/^NO FIT:\s*/i, '').trim() };
  }

  const inventedMatch = raw.match(/^\s*INVENTED:\s*([^\n]+)/m);
  const inventedRaw = inventedMatch ? inventedMatch[1].trim() : null;
  const inventedNote = inventedRaw && !/^nothing\.?$/i.test(inventedRaw) ? inventedRaw : null;

  return { kind: 'built', htmlContent: extractHtml(raw), inventedNote };
}

/** Refine output: a CHANGED line, then the full updated HTML. */
export function parseRefineOutput(raw: string): { htmlContent: string; changeNote: string | null } {
  const changeMatch = raw.match(/^\s*CHANGED:\s*([^\n]+)/);
  return {
    htmlContent: extractHtml(raw),
    changeNote: changeMatch ? changeMatch[1].trim() : null,
  };
}

/**
 * The partial document to paint while the build streams: everything from the
 * doctype onward, or null until the doctype has arrived.
 */
export function partialHtml(raw: string): string | null {
  const idx = raw.search(/<!doctype\s+html/i);
  return idx === -1 ? null : raw.slice(idx);
}

function hasDoctype(text: string): boolean {
  return /<!doctype\s+html/i.test(text);
}
